/**
 * HTTP Client
 *
 * Functional port of the Angular `CommonInterceptorInterceptor`:
 *   - Gated by an `interceptorEnabled` flag flipped on by `main.tsx` once
 *     SSO bootstrap finishes. Until then requests go out as-is.
 *   - Every authenticated request preflights `sso.updateToken(30)` so the
 *     token on the wire is guaranteed valid for at least 30 s.
 *   - The Authorization header reads `sso.token` first, falling back to the
 *     persisted `token` key in localStorage for the brief window between
 *     page reload and `sso.init()` completing.
 *   - Response bodies of the shape `{ Response: ... }` are unwrapped so
 *     callers see the inner payload directly (Notify backend envelope).
 *   - **No auto-logout on 401.** The decision to log out / re-auth lives in
 *     `authProvider.checkError` so 403 (authenticated, no permission)
 *     doesn't bounce the user out of the app, and 401 (auth genuinely lost)
 *     re-enters the Keycloak login flow rather than the portal.
 */

import { API_CONFIG } from '@/config/constants';
import sso from '@/auth/sso';
import { syncAuthToLocalStorage } from '@/auth/authSync';

let interceptorEnabled = false;

export const enableHttpInterceptor = (): void => {
  interceptorEnabled = true;
};

export const isHttpInterceptorEnabled = (): boolean => interceptorEnabled;

let inFlightCount = 0;
const inFlightListeners = new Set<(count: number) => void>();

const emitInFlight = (): void => {
  inFlightListeners.forEach((fn) => fn(inFlightCount));
};

export const subscribeHttpInFlight = (
  listener: (count: number) => void,
): (() => void) => {
  inFlightListeners.add(listener);
  listener(inFlightCount);
  return () => {
    inFlightListeners.delete(listener);
  };
};

export const getHttpInFlightCount = (): number => inFlightCount;

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  skipAuth?: boolean;
  /**
   * Don't count this request in the global in-flight counter (no blocking
   * GlobalBackdrop). For background lookups/prefetches whose loading state
   * is handled locally by the consuming component.
   */
  skipGlobalLoader?: boolean;
}

interface HttpResponse<T> {
  data: T;
  status: number;
  ok: boolean;
}

export class HttpError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.data = data;
  }
}

export const resolveAuthToken = async (): Promise<string | null> => {
  if (!interceptorEnabled) return null;
  try {
    const refreshed = await sso.updateToken(30);
    if (refreshed) syncAuthToLocalStorage(sso);
  } catch {
  }
  return localStorage.getItem('authToken') ?? sso.token ?? null;
};

const request = async <T>(
  endpoint: string,
  options: RequestOptions = {},
): Promise<HttpResponse<T>> => {
  if (options.skipGlobalLoader) {
    return runRequest<T>(endpoint, options);
  }
  inFlightCount += 1;
  emitInFlight();
  try {
    return await runRequest<T>(endpoint, options);
  } finally {
    inFlightCount -= 1;
    emitInFlight();
  }
};

const runRequest = async <T>(
  endpoint: string,
  options: RequestOptions = {},
): Promise<HttpResponse<T>> => {
  const {
    body,
    skipAuth = false,
    skipGlobalLoader: _skipGlobalLoader,
    headers: customHeaders,
    ...restOptions
  } = options;

  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

  const headers: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(customHeaders as Record<string, string> | undefined),
  };

  if (!skipAuth) {
    const token = await resolveAuthToken();
    if (token) {
      headers['accept'] = headers['accept'] ?? '*/*';
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const config: RequestInit = {
    ...restOptions,
    headers,
    body: isFormData
      ? (body as FormData)
      : body
        ? JSON.stringify(body)
        : undefined,
  };

  const url = endpoint.startsWith('http')
    ? endpoint
    : `${API_CONFIG.BASE_URL}${endpoint}`;

  const response = await fetch(url, config);

  const contentType = response.headers.get('content-type');
  const text = await response.text();
  const payload: unknown =
    contentType?.includes('application/json') && text
      ? JSON.parse(text)
      : text;

  if (!response.ok) {
    throw new HttpError(
      (payload as { message?: string })?.message || response.statusText,
      response.status,
      payload,
    );
  }

  return {
    data: payload as T,
    status: response.status,
    ok: response.ok,
  };
};

export const httpClient = {
  get: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'POST', body }),

  put: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'PUT', body }),

  patch: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'PATCH', body }),

  delete: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'DELETE' }),
};

// No-op for backwards compatibility with legacy callers.
export const markLoginSuccess = (): void => {
  /* no-op */
};

export default httpClient;
